"use client";

import { useState, useEffect, Fragment } from "react";
import type { JSX } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Menu, Transition } from "@headlessui/react";
import { GlobeAltIcon, ChevronDownIcon } from "@heroicons/react/16/solid";
import ButtonSignin from "./ButtonSignin";
import TopSponsorBanner from "./TopSponsorBanner";
import logo from "@/public/icon.png";
import config from "@/config";
import { useTranslations } from 'next-intl';
import { languages } from '../navigation'
import { aiNewsPath, isAiNewsRssLocale } from '@/libs/ai-news-rss';
import { getAiModelsNavLabel } from '@/libs/ai-models-i18n';

export type LocaleLink = {
  locale: string;
  href: string;
};

type HeaderProps = {
  locale: string;
  localeLinks?: LocaleLink[];
  showSponsorBanner?: boolean;
};

const removeLanguagePrefix = (path: string) => {
  const segments = path.split('/').filter(segment => segment !== '');
  if (languages.some(lang => lang.locale === segments[0])) {
    return '/' + segments.slice(1).join('/');
  }
  return path;
};

const Header = ({ locale, localeLinks, showSponsorBanner = true }: HeaderProps) => {
  const t = useTranslations('Header');
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const languageOptions = localeLinks
    ? localeLinks.flatMap((localeLink) => {
        const language = languages.find((item) => item.locale === localeLink.locale);
        return language ? [{ ...language, href: localeLink.href }] : [];
      })
    : languages.map((language) => ({ ...language, href: null }));

  const cta: JSX.Element = <ButtonSignin text={t('login')} extraStyle="btn-primary" />;
  const aiToolsHref = locale === 'en' ? '/ai-tools' : `/${locale}/ai-tools`;
  const aiModelsHref = locale === 'en' ? '/ai-model' : `/${locale}/ai-model`;
  const aiChatHref = locale === 'en' ? '/ai-chat' : `/${locale}/ai-chat`;
  const pricingHref = locale === 'en' ? '/pricing' : `/${locale}/pricing`;
  const submitHref = locale === 'en' ? '/submit' : `/${locale}/submit`;
  const aiNewsHref = aiNewsPath(isAiNewsRssLocale(locale) ? locale : 'en');

  const links: { href: string; label: string; badge?: string; }[] = [
    { href: aiChatHref, label: 'AI Chat', badge: 'FREE' },
    { href: aiToolsHref, label: t('aitools') },
    { href: aiModelsHref, label: getAiModelsNavLabel(locale) },
    { href: "/skills", label: "Agent Skills" },
    { href: aiNewsHref, label: t('aiNews') },
    { href: pricingHref, label: 'Pricing' },
    { href: submitHref, label: t('submit') },
  ];

  useEffect(() => {
    setIsOpen(false);
  }, [pathname, searchParams]);

  return (
    <>
      {showSponsorBanner && <TopSponsorBanner locale={locale} />}
      <header className="bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-between h-20" aria-label="Global">
            <div className="flex items-center flex-shrink-0">
              <Link className="flex items-center gap-3" href="/" title={`${config.appName} homepage`}>
                <Image
                  src={logo}
                  alt={`${config.appName} logo`}
                  className="w-10 h-10 rounded-full"
                  placeholder="blur"
                  priority={true}
                  width={40}
                  height={40}
                />
                <span className="font-extrabold text-xl">{config.appName}</span>
              </Link>
            </div>

            <div className="hidden xl:flex xl:items-center xl:justify-center xl:flex-1 xl:gap-1 2xl:gap-3">
              {links.map((link) => (
                <Link
                  href={link.href}
                  key={link.href}
                  aria-current={pathname === link.href ? 'page' : undefined}
                  className="relative inline-flex items-center rounded-md px-2 py-2 text-sm font-medium text-white transition duration-300 ease-in-out hover:bg-white hover:bg-opacity-20 2xl:px-3"
                >
                  {link.label}
                  {link.badge && (
                    <span className="absolute -right-2 -top-1 rounded-full bg-yellow-300 px-1.5 py-0.5 text-[9px] font-extrabold leading-none tracking-[0.05em] text-yellow-950 shadow-sm">
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            <div className="hidden xl:flex xl:items-center xl:justify-end xl:gap-4">
              {cta}

              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-purple-600 focus:ring-white transition duration-300 ease-in-out">
                  <GlobeAltIcon className="w-5 h-5 mr-2" />
                  {locale == 'default' ? 'EN' : locale.toUpperCase()}
                  <ChevronDownIcon className="ml-2 h-5 w-5" aria-hidden="true" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-30 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {languageOptions.map((item) => {
                        const hrefValue = item.href || `/${item.locale}${removeLanguagePrefix(pathname)}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
                        return (
                          <Menu.Item key={item.locale}>
                            <Link href={hrefValue} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                              {item.language}
                            </Link>
                          </Menu.Item>
                        );
                      })}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>

            <div className="flex xl:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-gray-200 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setIsOpen(true)}
              >
                <span className="sr-only">打开主菜单</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </nav>
        </div>

        {/* 移动端菜单 */}
        <div className={`xl:hidden ${isOpen ? "block" : "hidden"}`}>
          <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-50 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
          <div className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <Link className="flex items-center gap-2" title={`${config.appName} homepage`} href="/" onClick={() => setIsOpen(false)}>
                <Image
                  src={logo}
                  alt={`${config.appName} logo`}
                  className="w-8 h-8 rounded-full"
                  placeholder="blur"
                  priority={true}
                  width={32}
                  height={32}
                />
                <span className="font-extrabold text-lg text-gray-900">{config.appName}</span>
              </Link>
              <button type="button" className="text-gray-400 hover:text-gray-500" onClick={() => setIsOpen(false)}>
                <span className="sr-only">关闭菜单</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-4 py-6">
              <div className="flex flex-col space-y-4">
                {links.map((link) => (
                  <Link
                    href={link.href}
                    key={link.href}
                    className="relative flex w-fit items-center text-base font-medium text-gray-900 hover:text-blue-600"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                    {link.badge && (
                      <span className="absolute -right-10 -top-1 rounded-full bg-yellow-300 px-1.5 py-0.5 text-[9px] font-extrabold leading-none tracking-[0.05em] text-yellow-950 shadow-sm">
                        {link.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                {cta}
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
